import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { EnvelopeSimple, Sparkle, ArrowUUpLeft, DownloadSimple } from '@phosphor-icons/react';

/**
 * DocMailsPanel — Toont alle mailverkeer gekoppeld aan een document/factuur.
 * Backend: GET /api/documenten/{docId}/mails (mail_hub.py)
 * Match-criteria backend-side:
 *   1) document_id direct
 *   2) factuurnummer in subject/text_body (regex)
 *   3) klant_id van het document
 *   4) klant.email in from_email/to_emails
 */
const CATEGORY_BADGES = {
  factuur:    { label: 'Factuur',    color: 'bg-red-100 text-red-700 border-red-300' },
  reply:      { label: 'Antwoord',   color: 'bg-blue-100 text-blue-700 border-blue-300' },
  vraag:      { label: 'Vraag',      color: 'bg-purple-100 text-purple-700 border-purple-300' },
  klacht:     { label: 'Klacht',     color: 'bg-orange-100 text-orange-700 border-orange-300' },
  betaling:   { label: 'Betaling',   color: 'bg-green-100 text-green-700 border-green-300' },
  bestelling: { label: 'Bestelling', color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
  marketing:  { label: 'Marketing',  color: 'bg-gray-100 text-gray-600 border-gray-300' },
  spam:       { label: 'Spam',       color: 'bg-gray-100 text-gray-500 border-gray-300' },
  outbound:   { label: 'Verzonden',  color: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  other:      { label: 'Overig',     color: 'bg-gray-100 text-gray-600 border-gray-300' },
};

export default function DocMailsPanel({ docId }) {
  const [items, setItems] = useState([]);
  const [docNr, setDocNr] = useState('');
  const [klantEmail, setKlantEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [premiumBlocked, setPremiumBlocked] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    if (!docId) return;
    setLoading(true);
    try {
      const r = await API.get(`/documenten/${docId}/mails`);
      setItems(r.data.items || []);
      setDocNr(r.data.doc_nr || '');
      setKlantEmail(r.data.klant_email || '');
      setPremiumBlocked(false);
    } catch (e) {
      if (e.response?.status === 402) setPremiumBlocked(true);
      setItems([]);
    }
    setLoading(false);
  }, [docId]);

  useEffect(() => { load(); }, [load]);

  const openMail = async (mid) => {
    setSelectedLoading(true);
    setSelected({ id: mid });
    try {
      const r = await API.get(`/mail/messages/${mid}`);
      setSelected(r.data);
      setItems(arr => arr.map(m => m.id === mid ? { ...m, is_read: true } : m));
    } catch (e) {
      alert('Bericht laden mislukt: ' + (e.response?.data?.detail || e.message));
      setSelected(null);
    }
    setSelectedLoading(false);
  };

  const downloadAtt = async (att) => {
    try {
      const r = await API.get(`/mail/messages/${selected.id}/attachment/${att.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: att.mime }));
      const a = document.createElement('a');
      a.href = url; a.download = att.filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      alert('Download mislukt');
    }
  };

  if (premiumBlocked) {
    return (
      <div className="bg-white border border-gray-200" data-testid="doc-mails-premium-block">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 text-sm font-bold"><EnvelopeSimple size={16} className="text-[#8B5CF6]" /> Mailverkeer</div>
          <a href="/instellingen" className="text-xs text-purple-700 hover:underline">🔒 Pro-feature — upgrade</a>
        </div>
        <div className="p-4 text-xs text-gray-500">
          Koppel een mailbox via <a href="/mail" className="text-purple-700 hover:underline">Mail Hub</a> om alle correspondentie met deze klant per factuur te zien.
        </div>
      </div>
    );
  }

  return (
    <div className="mail-content bg-white border border-gray-200" data-testid="doc-mails-sectie">
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2 text-sm font-bold">
          <EnvelopeSimple size={16} className="text-[#8B5CF6]" />
          Mailverkeer
          {items.length > 0 && <span className="text-xs font-normal text-gray-500">({items.length} bericht{items.length === 1 ? '' : 'en'})</span>}
          {docNr && <span className="text-[10px] font-normal text-gray-400">• factuurnr {docNr}{klantEmail ? ` • ${klantEmail}` : ''}</span>}
        </div>
        <div className="flex items-center gap-2">
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); load(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); load(); } }}
            className="text-[11px] text-[#002FA7] hover:underline cursor-pointer"
            data-testid="doc-mails-refresh"
          >🔄 Ververs</span>
          <span className="text-xs text-gray-400">{collapsed ? '▶' : '▼'}</span>
        </div>
      </button>
      {!collapsed && (
        <div className="p-3" data-testid="doc-mails-content">
          {loading && items.length === 0 && <div className="text-center text-gray-400 py-6 text-sm">⏳ Mails laden...</div>}
          {!loading && items.length === 0 && (
            <div className="text-center text-gray-400 py-6 text-xs">
              <div className="text-2xl mb-1">📭</div>
              Geen mailverkeer gevonden voor deze factuur.
              {docNr && <div className="text-[10px] mt-1">Gezocht op factuurnr {docNr} + klant-email {klantEmail || '—'}</div>}
            </div>
          )}
          {items.length > 0 && (
            <div className="flex gap-3" style={{ minHeight: 280 }}>
              <div className="w-1/2 border border-gray-200 rounded overflow-y-auto" style={{ maxHeight: 420 }}>
                {items.map(m => {
                  const isActive = selected?.id === m.id;
                  const isOut = m.direction === 'outbound';
                  return (
                    <button
                      key={m.id}
                      onClick={() => openMail(m.id)}
                      className={`w-full text-left px-2 py-1.5 border-b text-[11px] transition-colors ${isActive ? 'bg-[#EEF3FF] border-l-2 border-l-[#002FA7]' : 'hover:bg-gray-50'} ${!m.is_read && !isOut ? 'font-semibold bg-blue-50/30' : ''}`}
                      data-testid={`doc-mail-${m.id}`}
                    >
                      <div className="flex items-center justify-between gap-1 mb-0.5">
                        <span className="truncate flex items-center gap-1">
                          <span className={`text-[9px] px-1 rounded ${isOut ? 'bg-indigo-100 text-indigo-700' : 'bg-green-100 text-green-700'}`}>{isOut ? '→' : '←'}</span>
                          <span className="text-gray-700 truncate">{isOut ? (m.to_emails || []).join(', ') : (m.from_name || m.from_email)}</span>
                        </span>
                        <span className="text-[9px] text-gray-400 flex-shrink-0">{(m.datum || '').slice(5, 16).replace('T', ' ')}</span>
                      </div>
                      <div className="text-gray-900 truncate">{m.subject || '(geen onderwerp)'}</div>
                      <div className="flex items-center gap-1 mt-0.5">
                        {m.ai_category && CATEGORY_BADGES[m.ai_category] && (
                          <span className={`text-[8px] px-1 rounded border ${CATEGORY_BADGES[m.ai_category].color}`}>{CATEGORY_BADGES[m.ai_category].label}</span>
                        )}
                        {(m.attachments || []).length > 0 && <span className="text-[8px] text-gray-400">📎 {m.attachments.length}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="flex-1 border border-gray-200 rounded overflow-y-auto p-3 text-xs" style={{ maxHeight: 420 }}>
                {selectedLoading && <div className="text-gray-400 text-center">⏳ Bericht laden...</div>}
                {!selected && !selectedLoading && (
                  <div className="text-gray-400 text-center pt-8"><div className="text-2xl opacity-40 mb-1">📧</div>Klik op een bericht om te lezen</div>
                )}
                {selected && !selectedLoading && selected.subject !== undefined && (
                  <div>
                    <div className="text-sm font-bold text-gray-900 mb-0.5">{selected.subject || '(geen onderwerp)'}</div>
                    <div className="text-[10px] text-gray-500 mb-2">
                      <span className="font-semibold">{selected.from_name || selected.from_email}</span> &lt;{selected.from_email}&gt; • {(selected.datum || '').slice(0, 16).replace('T', ' ')}
                    </div>
                    {selected.ai_summary && (
                      <div className="mb-2 px-2 py-1 bg-purple-50 border border-purple-200 rounded text-[10px] text-purple-800">
                        <Sparkle size={10} weight="fill" className="inline mr-1" />{selected.ai_summary}
                      </div>
                    )}
                    {(selected.attachments || []).length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-1">
                        {selected.attachments.map(att => (
                          <button
                            key={att.id}
                            onClick={() => downloadAtt(att)}
                            className="px-1.5 py-0.5 bg-gray-50 border border-gray-200 rounded text-[9px] hover:bg-gray-100 inline-flex items-center gap-1"
                            title={att.filename}
                          >
                            <DownloadSimple size={10} /> {att.filename.length > 25 ? att.filename.slice(0, 23) + '…' : att.filename}
                            {att.inkomende_document_id && <span className="text-green-600">✓</span>}
                          </button>
                        ))}
                      </div>
                    )}
                    {selected.html_body ? (
                      <iframe title="doc-mail" srcDoc={selected.html_body} sandbox="" className="w-full border-0 bg-white" style={{ minHeight: 200 }} />
                    ) : (
                      <pre className="whitespace-pre-wrap text-[11px] text-gray-800 bg-gray-50 p-2 rounded">{selected.text_body || selected.snippet}</pre>
                    )}
                    {selected.direction === 'inbound' && (
                      <div className="mt-2 pt-2 border-t text-right">
                        <a href="/mail" className="text-[10px] text-[#002FA7] hover:underline inline-flex items-center gap-1">
                          <ArrowUUpLeft size={10} /> Open in Mail Hub om te antwoorden
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
